/**
 * The realm's chronicle — the modal that answers `campaign.pendingEvent`.
 *
 * The strategic twin of `showChronicle` in `hud.ts`, and it holds to the same
 * two rules: there is no dismiss and Escape does not close it, because the
 * realm is frozen on the question and the only thing that unfreezes it is an
 * answer; and a choice the treasury cannot cover is shown, greyed, with the
 * price on it, because seeing the door you cannot afford is part of the
 * decision.
 *
 * One rule is its own, and it is the reason this is a file rather than another
 * `hud.ts` method: **the modal only blocks the world map.** `chronicleturn.ts`
 * is explicit that a pending question suspends the realm and *not* the town —
 * the masons do not down tools because the crown is being asked about an envoy.
 * So on the city screen this renders as a banner beside the travel bar instead,
 * and clicking it walks the player to the map, where the question is waiting.
 *
 * Everything drawn here comes out of `realmEventView` — resolved rules text,
 * prices, affordability — and everything sent back is one command. The UI never
 * touches the deck and never re-derives a context; that is the seam the sim
 * published, and holding to it is what lets a save taken with this open put it
 * straight back up.
 */

import { cmd, Cmd, type Command } from '../sim/commands.ts';
import type { World } from '../sim/world.ts';
import { realmEventView, type RealmEventView } from '../sim/campaign/chronicleturn.ts';

export interface RealmEventCallbacks {
  /** The answer, as a command for the realm's queue wherever the player stands. */
  onCommand(c: Command): void;
  /** The banner was clicked from the city: open the map, where the modal is. */
  onToRealm(): void;
}

export class RealmEventModal {
  private modal: HTMLElement;
  private note: HTMLElement;
  /**
   * `${id}#${turn}` of the question currently drawn, '' when none. The identity
   * key is what makes this idempotent per frame: the modal is rebuilt only when
   * the question changes, not sixty times a second — and a save loaded with a
   * question open sees a fresh key and reopens, exactly as the settlement's
   * chronicle does.
   */
  private shownKey = '';

  constructor(private cb: RealmEventCallbacks) {
    injectStyle();
    const root = document.getElementById('ui-root') as HTMLElement;
    this.modal = document.createElement('div');
    this.modal.id = 'realmevent';
    root.appendChild(this.modal);
    this.note = document.createElement('div');
    this.note.id = 'realmevent-note';
    this.note.style.display = 'none';
    this.note.onclick = () => this.cb.onToRealm();
    root.appendChild(this.note);
  }

  /**
   * Called every frame with the realm world (or null outside a campaign) and
   * whether the world map is the screen being looked at.
   */
  update(realm: World | null, onMap: boolean): void {
    const c = realm?.campaign;
    const p = c?.pendingEvent;
    if (!c || !p) {
      if (this.shownKey) this.hideAll();
      return;
    }

    const key = `${p.id}#${p.turn}`;
    if (onMap) {
      this.note.style.display = 'none';
      if (this.shownKey === key && this.modal.classList.contains('show')) return;
      const view = realmEventView(c);
      if (!view) return;
      this.shownKey = key;
      this.render(view);
    } else {
      this.modal.classList.remove('show');
      if (this.shownKey === key && this.note.style.display !== 'none') return;
      this.shownKey = key;
      const view = realmEventView(c);
      if (!view) return;
      this.note.innerHTML =
        `<span class="ren-mark">✦</span> <b>${esc(view.title)}.</b> ` +
        `The council waits on a word from the crown. <span class="ren-go">To the Realm</span>`;
      this.note.style.display = '';
    }
  }

  private hideAll(): void {
    this.shownKey = '';
    this.modal.classList.remove('show');
    this.modal.innerHTML = '';
    this.note.style.display = 'none';
  }

  private render(v: RealmEventView): void {
    const cards = v.choices
      .map((ch) => {
        const price = ch.price ? `<div class="crar">${esc(ch.price)}</div>` : '';
        return (
          `<div class="card${ch.affordable ? '' : ' locked'}" data-choice="${ch.index}">` +
          `<div class="cn">${esc(ch.label)}</div>` +
          `<div class="cr">${esc(ch.rules)}</div>` +
          price +
          `</div>`
        );
      })
      .join('');

    this.modal.innerHTML =
      `<div class="sheet"><h1>${esc(v.title)}</h1>` +
      `<div class="sub">${esc(v.kind)} · the realm is waiting on you</div>` +
      `<div class="cf">${esc(v.flavour)}</div>` +
      `<div class="cards">${cards}</div></div>`;
    this.modal.classList.add('show');

    for (const el of this.modal.querySelectorAll('[data-choice]')) {
      const node = el as HTMLElement;
      if (node.classList.contains('locked')) continue;
      node.onclick = () => {
        /**
         * The event id rides in `s` as the staleness guard the sim asks for; the
         * modal is left up. If the answer lands, `pendingEvent` clears and the
         * next frame's `update` takes the modal down — the sim is the one
         * authority on whether the question was answered, so a refused answer
         * (a race with an autosave-load, say) leaves the question on the table
         * rather than an unfrozen-looking realm that is still frozen.
         */
        this.cb.onCommand(cmd(Cmd.CampaignAnswerEvent, Number(node.dataset.choice), 0, 0, 0, v.id));
      };
    }
  }
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch] as string);
}

let styled = false;
function injectStyle(): void {
  if (styled) return;
  styled = true;
  const s = document.createElement('style');
  s.id = 'realmevent-style';
  s.textContent = `
    /* Above the world map (80) and its hover card (90). */
    #realmevent {
      position: absolute; inset: 0; z-index: 95; display: none;
      align-items: center; justify-content: center;
      background: rgba(6,6,7,0.82); pointer-events: auto;
    }
    #realmevent.show { display: flex; }
    #realmevent .sheet { width: min(920px, 92vw); max-height: 86vh; overflow-y: auto;
      background: var(--panel); border: 1px solid var(--panel-edge); padding: 22px 26px;
      box-shadow: 0 30px 90px rgba(0,0,0,0.8); }
    #realmevent h1 { font-size: 20px; letter-spacing: 0.26em; margin-bottom: 4px; }
    #realmevent .sub { font-size: 12px; color: var(--bone-deep); font-style: italic;
      margin-bottom: 18px; text-transform: capitalize; }
    #realmevent .sheet > .cf { font-size: 12.5px; font-style: italic; color: var(--bone-dim);
      line-height: 1.6; max-width: 72ch; margin-bottom: 18px; }
    /* The nudge on the city screen: under the travel bar, out of the HUD's way. */
    #realmevent-note { position: fixed; top: 88px; right: 288px; z-index: 40;
      background: rgba(16,15,13,0.92); border: 1px solid rgba(200,164,76,0.4);
      border-radius: 3px; padding: 8px 14px; cursor: pointer;
      font-family: Inter, system-ui, sans-serif; font-size: 12px; color: var(--bone-dim);
      max-width: 420px; transition: border-color 120ms, background 120ms; }
    #realmevent-note:hover { background: rgba(200,164,76,0.14); border-color: var(--gold); }
    #realmevent-note b { color: var(--bone); font-weight: 500; }
    #realmevent-note .ren-mark { color: var(--gold); }
    #realmevent-note .ren-go { color: var(--gold); letter-spacing: 0.14em;
      text-transform: uppercase; font-size: 10px; margin-left: 6px; white-space: nowrap; }
  `;
  document.head.appendChild(s);
}
